import {
  AccessibleOutlined,
  EmailOutlined,
  Phone,
  SecurityOutlined,
  WcOutlined
} from '@mui/icons-material'
import {
  Box,
  Grid,
  Paper,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomButton from '../../common/button/commonButton'
import HospitalDialog from '../../common/dialog/commonDialogBox'
import ReusableFormFields from '../../common/textfield/ReusableTextfield'
import { styles } from '../../commonStyle/formStyles'
import { fetchProfile, resetCurrentProfile, updateProfile } from '../../redux/admin/profileAction'
import { fetchRoles } from '../../redux/role/roleAction'
import { resetCurrentStaff } from '../../redux/staff/staffAction'

function StaffProfile() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    control
  } = useForm()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [specificErrors, setSpecificErrors] = useState({
    email: null,
    phone: null
  })

  const roleState = useSelector(state => state?.role?.fetchRoles || {})
  const fetchProfileState = useSelector(state => state?.profile?.fetchProfile)
  const updateProfileState = useSelector(state => state?.profile?.updateProfile)
  const loginValues = useSelector((state) => state.values?.loginValues || [])
  const userInfo = loginValues[0]?.user || {}

  useEffect(() => {
    if (userInfo?.user_id) {
      dispatch(fetchRoles())
      dispatch(fetchProfile(userInfo.user_id))
    }
  }, [dispatch, userInfo?.user_id])

  useEffect (() => {
    return () => {
      dispatch(resetCurrentProfile())
      }
      }, [dispatch])


  useEffect(() => {
    const profileData = fetchProfileState?.data?.data
    if (profileData) {
      setValue('name', profileData.name || '')
      setValue('email', profileData.email || '')
      setValue('phone', profileData.phone || '')
      setValue('gender_type', profileData.gender_type || '')
      setValue('role_id', profileData.role_id || '')
      setValue('status', profileData.status ? profileData.status.toString() : '1')
    }
  }, [fetchProfileState, setValue])

  // Handle profile update response
  useEffect(() => {
    if (updateProfileState) {
      const errorMsg = updateProfileState.errorMessage
  
      if (errorMsg?.email === 'email must be unique') {
        setSpecificErrors(prev => ({
          ...prev,
          email: 'Email already exists'
        }))
        setError('email', {
          type: 'manual',
          message: 'Email already exists'
        })
      } else if (errorMsg?.phone === 'phone must be unique') {
        setSpecificErrors(prev => ({
          ...prev,
          phone: 'Phone number already exists'
        }))
        setError('phone', {
          type: 'manual',
          message: 'Phone number already exists'
        })
      } else if (updateProfileState.success) {
        setIsSuccess(true)
        setOpenDialog(true)
        setDialogMessage('Profile updated successfully')
      } else if (!updateProfileState.success && updateProfileState.error) {
        // Build a user-friendly error string from errorMessage object
        const fieldErrors = Object.entries(errorMsg || {})
          .map(([field, msg]) => `${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`)
          .join('\n')
  
        setIsSuccess(false)
        setOpenDialog(true)
        setDialogMessage(fieldErrors || 'An error occurred')
      }
  
      dispatch(resetCurrentStaff())
    }
  }, [updateProfileState, setError, dispatch])
  
  const inputFields = [
    {
      name: 'name',
      label: 'Name',
      placeholder: 'Enter name',
      type: 'text',
      rules: { required: 'Name is required' },
      startIcon: <SecurityOutlined />
    },
    {
      name: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      type: 'email',
      rules: { required: 'Email is required' },
      startIcon: <EmailOutlined />
    },
    {
      name: 'phone',
      label: 'Phone Number',
      placeholder: 'Enter phone number',
      type: 'tel',
      rules: {
        required: 'Phone number is required',
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: 'Invalid phone number'
        },
        maxLength: {
          value: 10,
          message: 'Phone number should be 10 digits'
        }
      },
      startIcon: <Phone />
    },
    {
      name: 'gender_type',
      label: 'Gender',
      placeholder: 'Select gender',
      type: 'select',
      options: [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' }
      ],
      rules: { required: 'Gender is required' },
      startIcon: <WcOutlined />
    },
    // {
    //   name: 'role_id',
    //   label: 'Role',
    //   placeholder: 'Select Position',
    //   type: 'select',
    //   options:
    //     roleState?.Roles?.data?.map(role => ({
    //       value: role.id,
    //       label: role.name
    //     })) || [],
    //   rules: { required: 'Role is required' },
    //   startIcon: <WcOutlined />
    // },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Select status',
      type: 'select',
      options: [
        { value: '1', label: 'Active' },
        { value: '2', label: 'Inactive' },
        { value: '3', label: 'InProgress' }
      ],
      rules: { required: 'Status is required' },
      startIcon: <AccessibleOutlined />
    }
  ]

  const onSubmit = data => {
    const formData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      password:"",
      salt: "",
      gender_type: data.gender_type,
      role_id:"",
      status: data.status
    }

   
    setSpecificErrors({
      email: null,
      phone: null
    })

    clearErrors(['email', 'phone'])
    dispatch(updateProfile(userInfo?.user_id, formData))
  }

  const handleCancel = () => {
    // Reset form to initial values from profile data
    const profileData = fetchProfileState?.data?.data
    if (profileData) {
      setValue('name', profileData.name || '')
      setValue('email', profileData.email || '')
      setValue('phone', profileData.phone || '')
      setValue('gender_type', profileData.gender_type || '')
      setValue('role_id', profileData.role_id || '')
      setValue('status', profileData.status ? profileData.status.toString() : '1')
      
    }

    setSpecificErrors({
      email: null,
      phone: null
    })
    clearErrors()
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    if (isSuccess) {
      // Refresh profile data after successful update
      dispatch(fetchProfile(userInfo?.user_id))
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={12} md={12} lg={11} xl={11}>
            <Typography variant='h6' sx={styles.title}>
              My Profile
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
            <Box sx={styles.backButtonContainer}>
              <CustomButton
                type='button'
                text='Back'
                variant='contained'
                disableElevation
                size='small'
                onClick={handleBack}
              />
            </Box>
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              />
            </Grid>
          </Grid>
          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type='button'
              text='Cancel'
              variant='contained'
              disableElevation
              size='small'
              onClick={handleCancel}
            />
            <CustomButton
              type='submit'
              text='Submit'
              variant='contained'
              disableElevation
              size='small'
              disabled={updateProfileState?.inProgress}
              loading={updateProfileState?.inProgress}
            />
          </Box>
        </form>
      </Paper>
      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage}
        title={isSuccess ? "Profile Updated" : "Profile Update Error"}
      />
    </Box>
  )
}

export default StaffProfile